import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLatest } from 'react-use'
import { useDebouncedCallback } from 'use-debounce'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { useGetCrossClientQueryExecutionResultsQuery } from '../../../../store/crossClient/crossClient'
import { type CcqCard, type CcqCounts } from './ccqResultView'
import { CCQ_RESULT_INCLUDE, EMPTY_RESULTS, useCardFilter, useRefetchOnActions, useRefetchOnRevision } from './useCcqCardResults'
import {
  CCQ_FULL_LIST_SWAP_MS,
  CcqActionFilters,
  CcqFullListTransitions,
  CcqPanelOrigins,
  hasActionsFor,
  type CcqActionFilter,
  type CcqFullListSwap,
  type CcqPanelOrigin,
  type CcqPanelPosition,
} from './useCcqWorkspace'

const SEARCH_DEBOUNCE_MS = 300

export interface CcqCursorList {
  card: CcqCard
  page: number
  search: string
  filter: CcqActionFilter
}

// What the cursor starts from and reports back, in one shape, so it round-trips with the URL.
export interface CcqCursorState {
  list: CcqCursorList | null
  size: number
  selected: { resultId: string; card: CcqCard } | null
}

export interface CcqListCursorInputs {
  previews: Record<CcqCard, CrossClientQueryResult[]>
  counts: CcqCounts
  // Clients the advisor just moved out of a card. Their rows go from its list at once, ahead of the next read.
  isMovedOut: (resultId: string, card: CcqCard) => boolean
  // How many reads of each card's Preview have finished.
  previewReads: Partial<Record<CcqCard, number>>
  // Read once, on mount.
  restored: CcqCursorState
}

export interface CcqListRead {
  results: CrossClientQueryResult[]
  totalElements: number
  totalPages: number
  isFetching: boolean
  isError: boolean
  hasLoaded: boolean
}

export interface CcqSelectedClient {
  resultId: string
  card: CcqCard
  origin: CcqPanelOrigin
  // Its last row seen in the Preview or the list; null until one has shown it.
  row: CrossClientQueryResult | null
}

export interface CcqStepControl {
  isEnabled: boolean
  go: () => void
}

export interface CcqLastMove {
  seq: number
  // 0 when a client was opened, ±1 when stepped to.
  direction: -1 | 0 | 1
}

export interface CcqListCursor {
  isSelected: (resultId: string, card: CcqCard) => boolean
  open: (resultId: string, card: CcqCard) => void
  list: {
    card: CcqCard | null
    swap: CcqFullListSwap | null
    open: (card: CcqCard) => void
    close: () => void
    search: string
    // What the list is searched by: `search` once typing pauses.
    appliedSearch: string
    setSearch: (search: string) => void
    filter: CcqActionFilter
    setFilter: (filter: CcqActionFilter) => void
    page: number
    setPage: (page: number) => void
    size: number
    setSize: (size: number) => void
    read: CcqListRead
  }
  details: {
    selected: CcqSelectedClient | null
    position: CcqPanelPosition | null
    previous: CcqStepControl
    next: CcqStepControl
    close: () => void
    // The open client left its card: the one after them takes their place. False when there's none, so the panel can close.
    skip: () => boolean
    // The move behind a skip is saved, or undone: a client the skip still waits for is read now, so the read can't predate it.
    moveSettled: () => void
    lastMove: CcqLastMove
  }
  // Changes with the open card, the applied search and the filter.
  listKey: string
  state: CcqCursorState
}

interface CcqListReadState extends CcqListRead {
  // Zero-based page the shown rows belong to.
  pageIndex: number | null
  retry: () => void
}

interface CcqListReadParams {
  page: number
  size: number
  search: string
  hasActions: boolean | undefined
}

// A step whose page hasn't landed yet. `index` is the client's place in the card's order; `from` is the page it left, or the Preview its place came from.
interface CcqWaitingStep {
  card: CcqCard
  index: number
  from: number | 'preview'
  // A failure only counts as the step's once its page was seen loading; before that it's a cached one from an earlier read.
  hasSeenLoading: boolean
  // A skip's page is read only once the move is saved; until then the step waits, whatever the page shows.
  awaitsSave: boolean
}

// A skip whose next client is past the Preview's rows: the Preview read after the move brings them in at the moved client's place.
interface CcqPreviewWait {
  card: CcqCard
  index: number
  // The Preview's finished reads when the move settled; null until then.
  readsAtSettle: number | null
}

const useListRead = (card: CcqCard | null, params: CcqListReadParams, isFrozen: boolean): CcqListReadState => {
  const accountId = useAppSelector(selectAccountId)
  const executionId = useAppSelector(selectCrossClientResultsExecution)?.id
  const filter = useCardFilter(card)
  const search = params.search.trim()
  const args =
    executionId && filter
      ? {
          accountId,
          executionId,
          ...filter,
          ...(search ? { search } : {}),
          hasActions: params.hasActions,
          include: CCQ_RESULT_INCLUDE,
          page: params.page - 1,
          size: params.size,
        }
      : skipToken
  const isSkipped = args === skipToken
  const { data, currentData, isFetching, isError, refetch } = useGetCrossClientQueryExecutionResultsQuery(args)
  useRefetchOnRevision(refetch, isSkipped, isFrozen)
  useRefetchOnActions(refetch, isSkipped || params.hasActions === undefined, isFrozen)
  const retry = useCallback(() => {
    if (!isSkipped) {
      refetch()
    }
  }, [isSkipped, refetch])
  // `data` is the last fetch for any args: show it only while paging the same list (card, search, filter, size), never another card's.
  const listKey = card === null ? null : `${card}|${search}|${String(params.hasActions)}|${params.size}`
  const [dataListKey, setDataListKey] = useState<string | null>(null)
  if (currentData !== undefined && dataListKey !== listKey) {
    setDataListKey(listKey)
  }
  const shown = currentData ?? (dataListKey === listKey ? data : undefined)
  return useMemo(
    () => ({
      results: shown?.data ?? EMPTY_RESULTS,
      totalElements: shown?.page.totalElements ?? 0,
      totalPages: shown?.page.totalPages ?? 0,
      isFetching,
      isError,
      hasLoaded: shown !== undefined,
      pageIndex: shown?.page.number ?? null,
      retry,
    }),
    [shown, isFetching, isError, retry],
  )
}

export const useCcqListCursor = ({ previews, counts, isMovedOut, previewReads, restored }: CcqListCursorInputs): CcqListCursor => {
  const [listCard, setListCard] = useState<CcqCard | null>(restored.list?.card ?? null)
  const [swap, setSwap] = useState<CcqFullListSwap | null>(null)
  const swapTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [search, setSearchValue] = useState(restored.list?.search ?? '')
  const [appliedSearch, setAppliedSearch] = useState(restored.list?.search ?? '')
  const [filter, setFilterValue] = useState<CcqActionFilter>(restored.list?.filter ?? CcqActionFilters.ALL)
  const [page, setPageValue] = useState(restored.list?.page ?? 1)
  const [size, setSizeValue] = useState(restored.size)
  const [selected, setSelected] = useState(restored.selected)
  const [waiting, setWaiting] = useState<CcqWaitingStep | null>(null)
  const [previewWait, setPreviewWait] = useState<CcqPreviewWait | null>(null)
  // Read when a swap ends, which may be after the Preview changed.
  const previewsRef = useLatest(previews)
  const [lastMove, setLastMove] = useState<CcqLastMove>({ seq: 0, direction: 0 })

  const applySearch = useDebouncedCallback((value: string) => {
    setAppliedSearch(value)
    setPageValue(1)
    setWaiting(null)
  }, SEARCH_DEBOUNCE_MS)

  // Rows freeze while a client is open, so nothing moves under the advisor; the list catches up when the panel closes.
  const read = useListRead(listCard, { page, size, search: appliedSearch, hasActions: hasActionsFor(filter) }, selected !== null)
  const listRows = useMemo(
    () => (listCard === null ? read.results : read.results.filter((result) => !isMovedOut(result.id, listCard))),
    [read.results, listCard, isMovedOut],
  )
  // Only a read from before the move still holds the moved rows; a later one is already without them.
  const listTotal = read.totalElements - (read.results.length - listRows.length)
  const lastPage = Math.max(read.totalPages, 1)
  // A waiting step decides on a page past the end itself: back to the page it left, or to the Preview.
  if (listCard !== null && read.hasLoaded && !read.isFetching && page > lastPage && waiting === null) {
    setPageValue(lastPage)
  }

  // While a list swaps in, the list shown may still be another card's, so nothing it does is the waiting step's yet.
  const isWaitingListShown = waiting !== null && !waiting.awaitsSave && listCard === waiting.card && swap === null
  if (isWaitingListShown && read.isFetching && !waiting.hasSeenLoading) {
    setWaiting({ ...waiting, hasSeenLoading: true })
  }
  const isWaitingListSettled = isWaitingListShown && !read.isFetching
  const isWaitingPageShown = isWaitingListSettled && read.pageIndex === page - 1
  const waitingRow = isWaitingPageShown ? waiting.index - (page - 1) * size : -1
  const landsOnRow = isWaitingPageShown && waitingRow >= 0 && waitingRow < listRows.length
  const hasNothingToLandOn = isWaitingListSettled && !landsOnRow && (isWaitingPageShown || (read.isError && waiting.hasSeenLoading))
  // With nothing to land on, a step goes back to the page it left, or to the Preview it came from: the list closes again, once rendered.
  const goesBackToPreview = hasNothingToLandOn && waiting.from === 'preview'
  if (landsOnRow) {
    setWaiting(null)
    setSelected({ resultId: listRows[waitingRow].id, card: waiting.card })
  } else if (hasNothingToLandOn && waiting.from !== 'preview') {
    setWaiting(null)
    setPageValue(waiting.from)
  }

  // Once the move settles, a skip past the Preview lands as soon as a row takes the moved client's place: the next client
  // once the Preview is read again, or the client themself when the move was undone. A read without one opens the list there.
  const previewLanding = previewWait === null || previewWait.readsAtSettle === null ? null : (previews[previewWait.card][previewWait.index] ?? null)
  const missesPreviewLanding =
    previewWait !== null && previewWait.readsAtSettle !== null && previewLanding === null && (previewReads[previewWait.card] ?? 0) > previewWait.readsAtSettle
  if (previewWait !== null && previewLanding !== null) {
    setPreviewWait(null)
    setSelected({ resultId: previewLanding.id, card: previewWait.card })
  }

  // A client's place in its card's order. The shown page holds a window of it; the Preview, its first rows.
  // While a new page loads, the cursor keeps the old one's rows: their place comes from the page they belong to.
  const shownPage = (read.pageIndex ?? page - 1) + 1
  const firstShownIndex = (shownPage - 1) * size
  // Until the open card's list loads, its clients are found by their Preview index while that's where the list would put them:
  // not searched or filtered, and on page 1 or stepping in from the Preview. Once it has loaded, only its rows count, even when empty.
  const isInPreviewOrder = appliedSearch.trim() === '' && filter === CcqActionFilters.ALL
  const usesPreviewPlaces = isInPreviewOrder && (page === 1 || waiting?.from === 'preview')
  const showsListOf = (card: CcqCard) => card === listCard && (read.hasLoaded || !usesPreviewPlaces)
  const indexOf = (resultId: string, card: CcqCard): number => {
    if (showsListOf(card)) {
      const row = listRows.findIndex((result) => result.id === resultId)
      return row < 0 ? -1 : firstShownIndex + row
    }
    return previews[card].findIndex((result) => result.id === resultId)
  }
  const clientAt = (card: CcqCard, index: number): CrossClientQueryResult | null =>
    showsListOf(card)
      ? index >= firstShownIndex
        ? (listRows[index - firstShownIndex] ?? null)
        : null
      : card === listCard
        ? null
        : (previews[card][index] ?? null)
  const totalOf = (card: CcqCard): number => (showsListOf(card) ? listTotal : counts[card])

  const origin: CcqPanelOrigin | null = selected === null ? null : selected.card === listCard ? CcqPanelOrigins.FULL_LIST : CcqPanelOrigins.PREVIEW
  const selectedIndex = selected === null ? -1 : indexOf(selected.resultId, selected.card)
  const isWaiting = waiting !== null || previewWait !== null
  const canStepPrevious = selected !== null && selectedIndex > 0 && !isWaiting
  const canStepNext = selected !== null && selectedIndex >= 0 && !isWaiting && selectedIndex + 1 < totalOf(selected.card)
  const selectedTotal = selected === null ? 0 : totalOf(selected.card)
  const position = useMemo<CcqPanelPosition | null>(
    () => (selected === null || selectedIndex < 0 ? null : { position: selectedIndex + 1, total: selectedTotal }),
    [selected, selectedIndex, selectedTotal],
  )

  useEffect(
    () => () => {
      if (swapTimer.current !== null) {
        clearTimeout(swapTimer.current)
      }
    },
    [],
  )

  const runSwap = (transition: CcqFullListSwap, finish: () => void) => {
    if (swapTimer.current !== null) {
      clearTimeout(swapTimer.current)
    }
    setSwap(transition)
    swapTimer.current = setTimeout(() => {
      swapTimer.current = null
      setSwap(null)
      finish()
    }, CCQ_FULL_LIST_SWAP_MS)
  }

  const resetListParams = () => {
    applySearch.cancel()
    setSearchValue('')
    setAppliedSearch('')
    setFilterValue(CcqActionFilters.ALL)
  }

  // A step into the list from the Preview waits for its page; any other opening drops a waiting step.
  const openList = (card: CcqCard, stepIndex?: number) => {
    // Reopening the same list leaves its query as it was, so a read that failed wouldn't be tried again.
    const isRetry = card === listCard && read.isError
    setWaiting(stepIndex === undefined ? null : { card, index: stepIndex, from: 'preview', hasSeenLoading: false, awaitsSave: false })
    runSwap({ kind: CcqFullListTransitions.OPENING, card }, () => {
      if (isRetry) {
        read.retry()
      }
      setListCard(card)
      resetListParams()
      setPageValue(stepIndex === undefined ? 1 : Math.floor(stepIndex / size) + 1)
    })
  }

  const closeList = () => {
    if (listCard === null) {
      return
    }
    const card = listCard
    setWaiting(null)
    runSwap({ kind: CcqFullListTransitions.CLOSING, card }, () => {
      setListCard(null)
      resetListParams()
      setPageValue(1)
      setWaiting(null)
      // A client of the closing list stays open only if the Preview shows it.
      setSelected((current) =>
        current !== null && current.card === card && !previewsRef.current[card].some((result) => result.id === current.resultId) ? null : current,
      )
    })
  }

  // Reads the client at `index` of the open card, who isn't shown yet. A skip's read waits for the move to be saved.
  const readAt = (card: CcqCard, index: number, awaitsSave: boolean) => {
    if (card !== listCard) {
      if (awaitsSave) {
        setPreviewWait({ card, index, readsAtSettle: null })
      } else {
        openList(card, index)
      }
      return
    }
    setWaiting({ card, index, from: showsListOf(card) ? shownPage : 'preview', hasSeenLoading: false, awaitsSave })
    if (!awaitsSave) {
      readPage(Math.floor(index / size) + 1)
    }
  }

  // The page it needs is the one that's open but not shown (it failed, came back empty, or predates a move): read it again.
  const readPage = (targetPage: number) => {
    if (targetPage === page) {
      read.retry()
    } else {
      setPageValue(targetPage)
    }
  }

  const step = (direction: -1 | 1) => {
    if (selected === null || !(direction === 1 ? canStepNext : canStepPrevious)) {
      return
    }
    setLastMove((previous) => ({ seq: previous.seq + 1, direction }))
    const index = selectedIndex + direction
    const shown = clientAt(selected.card, index)
    if (shown === null) {
      readAt(selected.card, index, false)
    } else {
      setSelected({ resultId: shown.id, card: selected.card })
    }
  }

  const skip = (): boolean => {
    if (selected === null || selectedIndex < 0 || isWaiting || selectedIndex + 1 >= totalOf(selected.card)) {
      return false
    }
    setLastMove((previous) => ({ seq: previous.seq + 1, direction: 1 }))
    const shown = clientAt(selected.card, selectedIndex + 1)
    if (shown === null) {
      // Not read yet: with the moved client gone from every read, the next one takes their place.
      readAt(selected.card, selectedIndex, true)
    } else {
      setSelected({ resultId: shown.id, card: selected.card })
    }
    return true
  }

  const moveSettled = () => {
    if (previewWait !== null && previewWait.readsAtSettle === null) {
      setPreviewWait({ ...previewWait, readsAtSettle: previewReads[previewWait.card] ?? 0 })
    }
    if (waiting !== null && waiting.awaitsSave) {
      setWaiting({ ...waiting, awaitsSave: false })
      readPage(Math.floor(waiting.index / size) + 1)
    }
  }

  const commands = useLatest({
    open: (resultId: string, card: CcqCard) => {
      setWaiting(null)
      setPreviewWait(null)
      setSelected({ resultId, card })
      setLastMove((previous) => ({ seq: previous.seq + 1, direction: 0 }))
    },
    close: () => {
      setWaiting(null)
      setPreviewWait(null)
      setSelected(null)
    },
    openList: (card: CcqCard) => openList(card),
    closeList,
    setSearch: (value: string) => {
      setSearchValue(value)
      setWaiting(null)
      applySearch(value)
    },
    setFilter: (value: CcqActionFilter) => {
      setFilterValue(value)
      setPageValue(1)
      setWaiting(null)
    },
    setPage: (value: number) => {
      setPageValue(value)
      setWaiting(null)
    },
    setSize: (value: number) => {
      setSizeValue(value)
      setPageValue(1)
      setWaiting(null)
    },
    step,
    skip,
    moveSettled,
    openMissedPreviewLanding: () => {
      if (previewWait !== null) {
        setPreviewWait(null)
        openList(previewWait.card, previewWait.index)
      }
    },
  })

  // Stable for the life of the cursor; each call runs against the latest state.
  const stable = useMemo(
    () => ({
      open: (resultId: string, card: CcqCard) => commands.current.open(resultId, card),
      close: () => commands.current.close(),
      openList: (card: CcqCard) => commands.current.openList(card),
      closeList: () => commands.current.closeList(),
      setSearch: (value: string) => commands.current.setSearch(value),
      setFilter: (value: CcqActionFilter) => commands.current.setFilter(value),
      setPage: (value: number) => commands.current.setPage(value),
      setSize: (value: number) => commands.current.setSize(value),
      stepPrevious: () => commands.current.step(-1),
      stepNext: () => commands.current.step(1),
      skip: () => commands.current.skip(),
      moveSettled: () => commands.current.moveSettled(),
      openMissedPreviewLanding: () => commands.current.openMissedPreviewLanding(),
    }),
    [commands],
  )

  // Closing starts the swap's timer, which can't run while rendering; closing also drops the waiting step.
  useEffect(() => {
    if (goesBackToPreview) {
      stable.closeList()
    }
  }, [goesBackToPreview, stable])

  useEffect(() => {
    if (missesPreviewLanding) {
      stable.openMissedPreviewLanding()
    }
  }, [missesPreviewLanding, stable])

  const isSelected = useCallback((resultId: string, card: CcqCard) => selected?.resultId === resultId && selected.card === card, [selected])

  // The page being left stays in the read for the step's position, but its rows aren't shown while the next page loads.
  const isTurningPage = read.isFetching && read.pageIndex !== page - 1
  const publicRead = useMemo<CcqListRead>(
    () => ({
      results: isTurningPage ? EMPTY_RESULTS : listRows,
      totalElements: listTotal,
      totalPages: read.totalPages,
      isFetching: read.isFetching,
      isError: read.isError,
      hasLoaded: read.hasLoaded,
    }),
    [isTurningPage, listRows, listTotal, read.totalPages, read.isFetching, read.isError, read.hasLoaded],
  )

  const list = useMemo(
    () => ({
      card: listCard,
      swap,
      open: stable.openList,
      close: stable.closeList,
      search,
      appliedSearch,
      setSearch: stable.setSearch,
      filter,
      setFilter: stable.setFilter,
      page,
      setPage: stable.setPage,
      size,
      setSize: stable.setSize,
      read: publicRead,
    }),
    [listCard, swap, stable, search, appliedSearch, filter, page, size, publicRead],
  )

  const shownRow = selected === null ? null : ([...previews[selected.card], ...listRows].find((result) => result.id === selected.resultId) ?? null)
  // Kept once seen, so the panel still has the client while the list moves on (a search, a filter, another page).
  const [lastRow, setLastRow] = useState<CrossClientQueryResult | null>(null)
  if (shownRow !== null && shownRow !== lastRow) {
    setLastRow(shownRow)
  }
  const selectedRow = shownRow ?? (lastRow !== null && lastRow.id === selected?.resultId ? lastRow : null)
  const selectedClient = useMemo<CcqSelectedClient | null>(
    () => (selected === null || origin === null ? null : { resultId: selected.resultId, card: selected.card, origin, row: selectedRow }),
    [selected, origin, selectedRow],
  )
  const previous = useMemo(() => ({ isEnabled: canStepPrevious, go: stable.stepPrevious }), [canStepPrevious, stable])
  const next = useMemo(() => ({ isEnabled: canStepNext, go: stable.stepNext }), [canStepNext, stable])
  const details = useMemo(
    () => ({ selected: selectedClient, position, previous, next, close: stable.close, skip: stable.skip, moveSettled: stable.moveSettled, lastMove }),
    [selectedClient, position, previous, next, stable, lastMove],
  )

  const state = useMemo<CcqCursorState>(
    () => ({ list: listCard === null ? null : { card: listCard, page, search: appliedSearch, filter }, size, selected }),
    [listCard, page, appliedSearch, filter, size, selected],
  )

  return useMemo(
    () => ({ isSelected, open: stable.open, list, details, listKey: `${listCard ?? ''}|${appliedSearch}|${filter}`, state }),
    [isSelected, stable, list, details, listCard, appliedSearch, filter, state],
  )
}

export const CcqListCursorContext = createContext<CcqListCursor | null>(null)

export const useCcqCursor = (): CcqListCursor => {
  const value = useContext(CcqListCursorContext)
  if (value === null) {
    throw new Error('useCcqCursor must be used inside CcqWorkspaceProvider')
  }
  return value
}
